import React, { useContext, useRef, useState } from 'react'
import { Button, Checkbox, IconButton, InputGroup, LabelTooltip, Row, Select, Separator, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { ContentDelete } from '@fs/zion-icon'
import { HelpTip } from '@fs/zion-icon/dist/cjs/icons'
import SelectId from './SelectId'
import RelationshipChart from './RelationshipChart'
import useNotifications from '../../hooks/useNotifications'
import { calculateRelationship } from '../../services/personService'
import { RelationshipCalculatorContext } from './RelationshipCalculatorContext'
import ResyncTreeGraph from './ResyncTreeGraph'
import { PermissionsContext } from '../app/PermissionsContext'

const RelationshipCalculatorForm = () => {
  const { error } = useNotifications()
  const { t } = useTranslation()
  const { isRole } = useContext(PermissionsContext)

  const [hintCommonAncestorIds, setHintCommonAncestorIds] = useState([])

  const handleHintAncestorChange = (index, value) => {
    const newHintCommonAncestorIds = [...hintCommonAncestorIds]
    newHintCommonAncestorIds[index] = value
    setHintCommonAncestorIds(newHintCommonAncestorIds)
  }

  const addHintAncestorInput = () => {
    setHintCommonAncestorIds([...hintCommonAncestorIds, ''])
  }
  const context = useContext(RelationshipCalculatorContext)
  const { insetInlineStart, insetInlineEnd } = context
  const [relationships, setRelationships] = useState()
  const buttonFocusRef = useRef()
  const [selectedSecurityOption, setSelectedSecurityOption] = useState('AS_SOURCE_ID')
  const [selectedTraversalOption, setSelectedTraversalOption] = useState('STANDARD')
  const [includeAllMePersons, setIncludeAllMePersons] = useState(false)
  const [validIds, setValidIds] = useState({ left: insetInlineStart.validId, right: insetInlineEnd.validId })
  const securityOptions = [
    { value: 'AS_SOURCE_ID', label: t('person.calculator.asSourceId', 'As Source ID') },
    { value: 'AS_ADMIN', label: t('person.calculator.asAdmin', 'As Admin') },
  ]
  const traversalOptions = [
    { value: 'RESTRICTED', label: t('person.calculator.restricted', 'Restricted') },
    { value: 'ASIAN', label: t('person.calculator.asian', 'Asian') },
    { value: 'UNLIMITED_GENERATIONS', label: t('person.calculator.unlimitedGenerations', 'Unlimited Generations') },
    { value: 'STANDARD', label: t('person.calculator.standard', 'Standard') },
  ]

  const getCalculatedRelationship = (event) => {
    event.preventDefault()
    const leftPersonId = insetInlineStart.personId
    const rightPersonId = insetInlineEnd.personId

    if (leftPersonId === rightPersonId) {
      setRelationships({ relationSummary: 'Same Person' })
    } else {
      calculateRelationship(
        leftPersonId,
        rightPersonId,
        hintCommonAncestorIds,
        selectedSecurityOption,
        selectedTraversalOption,
        includeAllMePersons
      )
        .then((response) => setRelationships(response))
        .catch((err) => {
          error(
            t('queues.errors.findingCommonAncestor', {
              defaultValue: `Error finding common ancestor: ${err.message || ''}`,
              message: err.message,
            }),
            err
          )
        })
    }
  }

  const buttonCallback = (leftValidId, rightValidId) => {
    setValidIds({ left: leftValidId, right: rightValidId })
    const buttonState = !(leftValidId && rightValidId)
    setRelationships(null)
    if (!buttonState) buttonFocusRef.current.focus()
  }

  const removeHintAncestorInput = (index) => {
    const newHintCommonAncestorIds = hintCommonAncestorIds.filter((_, i) => i !== index)
    setHintCommonAncestorIds(newHintCommonAncestorIds)
  }

  const setTraversalOption = (value) => {
    setSelectedTraversalOption(value)
    if (value === 'UNLIMITED_GENERATIONS') {
      setSelectedSecurityOption('AS_ADMIN')
    } else {
      setSelectedSecurityOption('AS_SOURCE_ID')
    }
  }

  return (
    <InputGroup>
      <Row>
        <SelectId leftComponent buttonCallback={buttonCallback} />
        <SelectId leftComponent={false} buttonCallback={buttonCallback} />
        {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
          <ResyncTreeGraph
            alignY="nextToInput"
            sourceIds={[insetInlineStart.personId]}
            destinationIds={[insetInlineEnd.personId]}
            validIds={validIds}
          />
        )}
      </Row>
      <Row>
        <LabelTooltip
          alignY="nextToInput"
          target={<HelpTip />}
          label={
            <>
              <div css={{ textAlign: 'start' }}>
                {t(
                  'person.calculator.restrictedDescription',
                  'Restricted - Limits the traversal to 15 generations up and 15 generations down'
                )}
              </div>
              <Separator />
              <div css={{ textAlign: 'start' }}>
                {t('person.calculator.asianDescription', 'Asian - Unlimited traversal up and down for Asian lines')}
              </div>
              <Separator />
              <div css={{ textAlign: 'start' }}>
                {t(
                  'person.calculator.unlimitedGenerationsDescription',
                  'Unlimited Generations - Unlimited traversal up and down'
                )}
              </div>
              <Separator />
              <div css={{ textAlign: 'start', flex: 1 }}>
                {t('person.calculator.standardDescription', 'Standard - Up to 200AD and down from 1500AD')}
              </div>
            </>
          }
        />
        <Select
          id="traversalTypeSelect"
          options={traversalOptions}
          value={selectedTraversalOption}
          label={t('person.calculator.traversalType', 'Traversal Type')}
          onChange={(e) => setTraversalOption(e.target.value)}
        />
        <LabelTooltip
          alignY="nextToInput"
          target={<HelpTip />}
          label={
            <>
              <div css={{ textAlign: 'start' }}>
                {t(
                  'person.calculator.asAdminDescription',
                  'As Admin - Bypasses any restrictions that may be in place for the user'
                )}
              </div>
              <Separator />
              <div css={{ textAlign: 'start' }}>
                {t(
                  'person.calculator.asSourceIdDescription',
                  'As Source ID - Adheres to the restrictions on the source user'
                )}
              </div>
            </>
          }
        />

        <Select
          id="securityTypeSelect"
          options={securityOptions}
          value={selectedSecurityOption}
          label={t('person.calculator.securityType', 'Security Type')}
          onChange={(e) => setSelectedSecurityOption(e.target.value)}
        />
        <Checkbox
          name="includeAllMePersonsCheckbox"
          alignY="nextToInput"
          id="includeAllMePersonsCheckbox"
          label={t('person.calculator.includeAllMePersons', 'Include All Me Persons')}
          type="checkbox"
          checked={includeAllMePersons}
          onChange={(e) => setIncludeAllMePersons(e.target.checked)}
        />
      </Row>
      {hintCommonAncestorIds.map((hintAncestorId, index) => (
        <Row key={`row-${hintAncestorId}`}>
          <TextField
            type="text"
            value={hintAncestorId}
            onChange={(e) => handleHintAncestorChange(index, e.target.value)}
            placeholder={t('relationshipCalculator.hintCommonAncestorId', 'Hint Common Ancestor ID')}
          />
          <IconButton
            id={`deleteButton${index}`}
            Icon={ContentDelete}
            onClick={() => removeHintAncestorInput(index)}
            color="red"
            emphasis="high"
          />
        </Row>
      ))}
      <Button onClick={addHintAncestorInput}>{t('person.calculator.addHintAncestor', 'Add Hint Ancestor')}</Button>
      <Separator />
      <Button
        id="calculateRelationshipBtn"
        disabled={!(insetInlineStart.validId && insetInlineEnd.validId)}
        emphasis="high"
        ref={buttonFocusRef}
        onClick={(event) => getCalculatedRelationship(event)}
      >
        {t('person.calculator.calculate', 'Calculate')}
      </Button>
      {relationships && <RelationshipChart relationships={relationships} />}
    </InputGroup>
  )
}
export default RelationshipCalculatorForm
